import { Keyboard, KeyboardResize } from '@capacitor/keyboard'
import { Capacitor } from '@capacitor/core'

function isTextInput(element: HTMLElement): boolean {
  const tag = element.tagName.toLowerCase()
  const isEditable = (element as HTMLElement).isContentEditable
  const inputTypes = ['input', 'textarea']
  return isEditable || inputTypes.includes(tag)
}

function scrollElementIntoViewCentered(element: HTMLElement) {
  const rect = element.getBoundingClientRect()
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight
  const targetTop = rect.top + window.scrollY - (viewportHeight / 2 - rect.height / 2) - 16
  const top = Math.max(0, targetTop)
  window.scrollTo({ top, behavior: 'smooth' })
}

export function setupMobileInputAutoScroll(): () => void {
  if (!Capacitor.isNativePlatform()) {
    return () => {}
  }

  let focusedEl: HTMLElement | null = null
  let keyboardOpen = false
  let scrollTimeout: ReturnType<typeof setTimeout> | null = null
  let scrollRequested = false

  const onFocusIn = (e: FocusEvent) => {
    const target = e.target as HTMLElement | null
    if (!target || !isTextInput(target)) return
    focusedEl = target
    scrollRequested = true

    // Small delay to allow focus to settle
    if (scrollTimeout) clearTimeout(scrollTimeout)
    scrollTimeout = setTimeout(() => {
      if (keyboardOpen) {
        // If the keyboard is already open, scroll immediately and consume the request
        scrollElementIntoViewCentered(target)
        scrollRequested = false
      } else {
        // Only Android supports programmatic show()
        if (Capacitor.getPlatform() === 'android') {
          Keyboard.show().catch(() => {})
        }
        // Defer scrolling to keyboardDidShow
      }
    }, 50)
  }

  const subs: Array<{ remove: () => void }> = []

  Keyboard.addListener('keyboardWillShow', () => {
    keyboardOpen = true
  }).then(listener => subs.push(listener))
  Keyboard.addListener('keyboardDidShow', () => {
    // After keyboard opens, make sure the input is visible
    if (focusedEl && scrollRequested) {
      if (scrollTimeout) clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        scrollElementIntoViewCentered(focusedEl as HTMLElement)
        scrollRequested = false
      }, 50)
    }
  }).then(listener => subs.push(listener))
  Keyboard.addListener('keyboardWillHide', () => {
    keyboardOpen = false
    scrollRequested = false
  }).then(listener => subs.push(listener))
  Keyboard.addListener('keyboardDidHide', () => {
    keyboardOpen = false
    scrollRequested = false
  }).then(listener => subs.push(listener))

  document.addEventListener('focusin', onFocusIn, true)

  // Prefer native resize to avoid visual glitches when keyboard shows
  Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => {})

  return () => {
    document.removeEventListener('focusin', onFocusIn, true)
    subs.forEach(s => s.remove())
    if (scrollTimeout) clearTimeout(scrollTimeout)
  }
}


